import React from 'react';
import { motion } from 'framer-motion';
import { HomeModernIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { ListingFormData } from '../types';

interface AccessibilityStepProps {
  register: UseFormRegister<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
}

/**
 * AccessibilityStep Component
 *
 * Collects property accessibility information in compliance with the European Accessibility Act (EAA).
 * Enables landlords to declare accessibility features of their rental properties.
 *
 * EAA Compliance: Article 4 requires e-commerce platforms to enable sellers to provide
 * "information on the accessibility of the products and services being sold".
 *
 * @param {AccessibilityStepProps} props - Component props
 * @param {UseFormRegister<ListingFormData>} props.register - React Hook Form register function
 * @param {FieldErrors<ListingFormData>} props.errors - Form validation errors
 *
 * @accessibility
 * - Uses semantic HTML with fieldset and legend for grouping
 * - Provides clear labels for all checkboxes
 * - Includes descriptive help text for each feature
 * - Maintains keyboard navigation support
 * - Ensures WCAG 2.1 AA contrast ratios
 */
const AccessibilityStep: React.FC<AccessibilityStepProps> = ({
  register,
  errors
}) => {
  return (
    <motion.div
      key="accessibilityStep"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Optional:</strong> Help renters find accessible properties
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <HomeModernIcon className="w-6 h-6 mr-2 text-blue-500" />
        Accessibility Features
      </h2>

      <div className="space-y-6">
        {/* Informational banner */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <p className="text-sm text-blue-800">
            <strong>Why provide this information?</strong> Accessibility details
            help renters with disabilities find suitable properties.
          </p>
        </div>

        {/* Accessibility features fieldset */}
        <fieldset className="border border-gray-300 rounded-lg p-6 space-y-4">
          <legend className="text-lg font-medium text-gray-900 px-2">
            Select all accessibility features that apply
          </legend>

          {/* Step-free Entry */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('accessibility.hasStepFreeEntry')}
                type="checkbox"
                id="hasStepFreeEntry"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="hasStepFreeEntry"
                className="font-medium text-gray-700 cursor-pointer flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-1 text-green-500" />
                Step-free Entry
              </label>
              <p className="text-sm text-gray-500">
                No steps to enter the property; accessible via ramp or level
                entry
              </p>
            </div>
          </div>

          {/* Elevator Access */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('accessibility.hasElevatorAccess')}
                type="checkbox"
                id="hasElevatorAccess"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="hasElevatorAccess"
                className="font-medium text-gray-700 cursor-pointer flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-1 text-green-500" />
                Elevator Access
              </label>
              <p className="text-sm text-gray-500">
                Building has elevator access to all floors (for multi-story
                buildings)
              </p>
            </div>
          </div>

          {/* Roll-in Shower */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('accessibility.hasRollInShower')}
                type="checkbox"
                id="hasRollInShower"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="hasRollInShower"
                className="font-medium text-gray-700 cursor-pointer flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-1 text-green-500" />
                Roll-in Shower
              </label>
              <p className="text-sm text-gray-500">
                Bathroom has barrier-free shower with no step or threshold
              </p>
            </div>
          </div>

          {/* Wide Doorways */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('accessibility.wideDoorways')}
                type="checkbox"
                id="wideDoorways"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="wideDoorways"
                className="font-medium text-gray-700 cursor-pointer flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-1 text-green-500" />
                Wide Doorways
              </label>
              <p className="text-sm text-gray-500">
                All doorways are wheelchair accessible (minimum 32 inches/81cm
                wide)
              </p>
            </div>
          </div>

          {/* Visual Fire Alarm */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                {...register('accessibility.visualFireAlarm')}
                type="checkbox"
                id="visualFireAlarm"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
            </div>
            <div className="ml-3">
              <label
                htmlFor="visualFireAlarm"
                className="font-medium text-gray-700 cursor-pointer flex items-center"
              >
                <CheckCircleIcon className="w-5 h-5 mr-1 text-green-500" />
                Visual Fire Alarm
              </label>
              <p className="text-sm text-gray-500">
                Property has visual (strobe light) fire alarm system for
                deaf/hard of hearing residents
              </p>
            </div>
          </div>
        </fieldset>

        {/* Help text */}
        <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> Only select features that are currently
            present in the property. Providing accurate accessibility
            information helps renters make informed decisions.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AccessibilityStep;
